import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserPlus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Table } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import api from "../../services/api";
import { ROLE_BADGE_VARIANT } from "./utils";
import { AddUserModal } from "./components/AddUserModal";
import { ChangeRoleModal } from "./components/ChangeRoleModal";
import { RemoveUserModal } from "./components/RemoveUserModal";

export default function UsersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [roleTarget, setRoleTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const loadRows = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get("/users")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setError(t("users.load_error")))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  if (user && user.role !== "ADMIN") {
    return <Navigate to="/dashboard/invoices" replace />;
  }

  const columns = [
    {
      key: "name",
      label: t("users.col_name"),
      render: (_v, row) => `${row.firstName || ""} ${row.lastName || ""}`.trim(),
    },
    { key: "email", label: t("users.col_email") },
    {
      key: "role",
      label: t("users.col_role"),
      render: (value) => (
        <Badge variant={ROLE_BADGE_VARIANT[value] || "preparing"}>
          {t(`users.role_${(value || "viewer").toLowerCase()}`)}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: t("users.col_actions"),
      align: "right",
      render: (_v, row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => setRoleTarget(row)}
            title={t("users.change_role_action")}
            className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <Pencil size={14} strokeWidth={1.75} />
          </button>
          <button
            onClick={() => setRemoveTarget(row)}
            title={t("users.remove_user_action")}
            className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600"
          >
            <Trash2 size={14} strokeWidth={1.75} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[18px] font-bold text-slate-900">
            {t("users.page_title")}
          </h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {t("users.page_subtitle")}
          </p>
        </div>
        <Button icon={UserPlus} onClick={() => setAddOpen(true)}>
          {t("users.add_user_button")}
        </Button>
      </div>

      {error && <p className="text-[12.5px] text-rose-600">{error}</p>}

      <Table
        columns={columns}
        rows={rows}
        loading={loading}
        emptyState={
          <div className="py-10 text-center text-[12.5px] text-slate-400">
            {t("users.empty_state")}
          </div>
        }
      />

      <AddUserModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={loadRows}
      />
      <ChangeRoleModal
        open={Boolean(roleTarget)}
        user={roleTarget}
        onClose={() => setRoleTarget(null)}
        onUpdated={(id, role) => {
          setRows((prev) => prev.map((r) => (r._id === id ? { ...r, role } : r)));
        }}
      />
      <RemoveUserModal
        open={Boolean(removeTarget)}
        user={removeTarget}
        onClose={() => setRemoveTarget(null)}
        onRemoved={(id) => setRows((prev) => prev.filter((r) => r._id !== id))}
      />
    </div>
  );
}
